import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type {
	DataSourceLoadOptions,
	ReviewToken,
	ReviewAdditionalFiltering,
	EnumToken,
	ReviewInfoToken,
	ProductToken,
	ReviewReportToken,
	ReviewStatus
} from '@sleed/bizcommerce-api';
import { productsApi, reviewsApi } from '~/lib/api/client';
import type { GridSort } from '~/lib/grid/sort';
import { chunkIds, endOfDayIso, reviewSortOptions, type ReviewKind } from '~/lib/reviews/reviews';

export type { ReviewKind } from '~/lib/reviews/reviews';

export interface ReviewsListParams {
	skip?: number;
	take?: number;
	search?: string;
	/** Selected status ids (ReviewStatus values). */
	statuses?: number[];
	ratingFrom?: number | null;
	ratingTo?: number | null;
	dateFrom?: string | null;
	dateTo?: string | null;
	/** Header sort; none → the server's newest-first. */
	sort?: GridSort | null;
	/** Only reviews with reports not resolved yet. */
	reported?: boolean;
	/** What the reviews are about (BizwebEntityType values); none → every type. */
	targetTypes?: number[];
}

export interface ReviewsListResult {
	items: ReviewToken[];
	totalCount: number;
}

/** Rows one export asks for (the legacy grid exported the filtered set; a cut export says so). */
export const REVIEWS_EXPORT_LIMIT = 5000;

export const reviewKeys = {
	all: ['reviews'] as const,
	list: (kind: ReviewKind, params: unknown) => ['reviews', kind, 'list', params] as const,
	statuses: () => ['reviews', 'statuses'] as const,
	info: (id: number) => ['reviews', 'info', id] as const,
	groupedProducts: (productId: number) => ['reviews', 'grouped-products', productId] as const,
	reports: (reviewId: number) => ['reviews', 'reports', reviewId] as const,
	pendingCount: () => ['reviews', 'pending-count'] as const
};

/** Exported for the tests: the grid's filters as the server's `additionalFiltering`. */
export function buildReviewFiltering(p: ReviewsListParams): ReviewAdditionalFiltering {
	const f: ReviewAdditionalFiltering = {};
	if (p.search?.trim()) f.searchText = p.search.trim();
	if (p.statuses?.length) f.statuses = p.statuses;
	if (p.ratingFrom != null) f.rating_from = p.ratingFrom;
	if (p.ratingTo != null) f.rating_to = p.ratingTo;
	if (p.dateFrom) f.reviewDate_from = p.dateFrom;
	if (p.dateTo) f.reviewDate_to = endOfDayIso(p.dateTo);
	if (p.reported) f.reported = true;
	if (p.targetTypes?.length) f.to_entities = p.targetTypes;
	return f;
}

/**
 * Exported for the tests: one page, or an export (`isExportRequest`), which lifts the server's 500-row page clamp up to
 * its export ceiling (REVIEWS_EXPORT_LIMIT).
 */
export function buildReviewLoadOptions(sort: GridSort | null, skip: number, take: number, isExport = false): DataSourceLoadOptions {
	const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true, ...reviewSortOptions(sort) };
	if (isExport) loadOptions.isExportRequest = 1;
	return loadOptions;
}

async function loadReviews(
	kind: ReviewKind,
	params: ReviewsListParams,
	skip: number,
	take: number,
	isExport = false
): Promise<ReviewsListResult> {
	const loadOptions = buildReviewLoadOptions(params.sort ?? null, skip, take, isExport);
	const request = { loadOptions, additionalFiltering: buildReviewFiltering(params) };
	const res = kind === 'customer' ? await reviewsApi.getAllCustomerReview(request) : await reviewsApi.getAllContributorReview(request);
	return { items: res.data.data ?? [], totalCount: res.data.totalCount ?? 0 };
}

/**
 * Server-paged reviews via the legacy DevExtreme loadOptions envelope (getAllCustomerReview /
 * getAllContributorReview → ReviewQueryResponse { data, totalCount }). `keepPreviousData` avoids the
 * grid flashing empty on page/filter changes (mirrors the products list).
 */
export function useReviewsList(kind: ReviewKind, params: ReviewsListParams = {}) {
	const { skip = 0, take = 25, ...rest } = params;
	return useQuery<ReviewsListResult>({
		queryKey: reviewKeys.list(kind, { skip, take, ...rest }),
		placeholderData: keepPreviousData,
		queryFn: () => loadReviews(kind, params, skip, take)
	});
}

/**
 * The filtered, sorted rows for the CSV export (not just the page on screen), capped at REVIEWS_EXPORT_LIMIT; the
 * unclamped `totalCount` lets a cut export say so.
 */
export function fetchReviewsForExport(kind: ReviewKind, params: Omit<ReviewsListParams, 'skip' | 'take'>): Promise<ReviewsListResult> {
	return loadReviews(kind, params, 0, REVIEWS_EXPORT_LIMIT, true);
}

/** Review status lookup (Pending/Accepted/Declined/…) for the status filter + edit dropdown. */
export function useReviewStatuses() {
	return useQuery<EnumToken[]>({
		queryKey: reviewKeys.statuses(),
		queryFn: async () => (await reviewsApi.getReviewStatuses()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

/**
 * A review's details panel (GetReviewInfo — the legacy "details" popup): the reviewed item of any type (category and
 * publisher for a product) and the author (a customer with their orders and reviews, or a contributor).
 */
export function useReviewInfo(id: number | null) {
	return useQuery<ReviewInfoToken>({
		queryKey: reviewKeys.info(id ?? 0),
		queryFn: async () => (await reviewsApi.getReviewInfo(id!)).data,
		enabled: id != null && id > 0
	});
}

/** Products sharing the review's product GroupCode (its colour/size siblings), excluding the product itself. */
export function useGroupedProducts(productId: number | null) {
	return useQuery<ProductToken[]>({
		queryKey: reviewKeys.groupedProducts(productId ?? 0),
		queryFn: async () => (await productsApi.getGroupedProducts(productId!)).data.items ?? [],
		enabled: productId != null && productId > 0
	});
}

export function useReviewMutations(kind: ReviewKind) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: reviewKeys.all });
	// Update preserves the full token (merge-style parity with the legacy grid) — callers spread the
	// original row and override the moderated fields (st / rating / iv / si / isFeatured / review).
	const update = useMutation({
		mutationFn: async (token: ReviewToken) =>
			(kind === 'customer' ? await reviewsApi.updateCustomerProductReview(token) : await reviewsApi.updateContributorProductReview(token))
				.data,
		onSuccess: invalidate
	});
	// Only editorial reviews are created in admin; customer comments come from the storefront.
	const create = useMutation({
		mutationFn: async (token: ReviewToken) => (await reviewsApi.insertContributorProductReview(token)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await reviewsApi.remove(id)).data, onSuccess: invalidate });
	return { update, create, remove };
}

export interface DuplicateResult {
	productId: number;
	error?: unknown;
}

/**
 * Copies an editorial review onto each product, one call per product (the endpoint takes one target). Every product is
 * tried; the result lists which failed — the server answers 400 when that product already holds the same review.
 */
export function useDuplicateReview() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, productIds }: { id: number; productIds: number[] }): Promise<DuplicateResult[]> => {
			const results: DuplicateResult[] = [];
			for (const productId of productIds) {
				try {
					await reviewsApi.duplicateContributorProductReview({ id, to_id: productId });
					results.push({ productId });
				} catch (error) {
					results.push({ productId, error });
				}
			}
			return results;
		},
		onSettled: () => qc.invalidateQueries({ queryKey: reviewKeys.all })
	});
}

/** Rebuilds every product's rating/review count from the accepted reviews (ReCalculateProductReviews). */
export function useRecalculateRatings() {
	return useMutation({ mutationFn: async () => (await reviewsApi.reCalculateProductReviews()).data });
}

/**
 * Bulk moderation: one status for many reviews (BulkSetReviewStatus) or a bulk delete (BulkDeleteReviews), sent in
 * calls of at most 500 ids. Resolves to how many reviews the server changed. Approving customer comments sends the
 * "accepted" e-mail and the loyalty points server-side, as a single approval does.
 */
export function useReviewBulkActions() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: reviewKeys.all });
	const setStatus = useMutation({
		mutationFn: async ({ ids, st }: { ids: number[]; st: ReviewStatus }) => {
			let changed = 0;
			for (const chunk of chunkIds(ids)) changed += (await reviewsApi.bulkSetReviewStatus({ ids: chunk, st })).data ?? 0;
			return changed;
		},
		onSettled: invalidate
	});
	const remove = useMutation({
		mutationFn: async (ids: number[]) => {
			let deleted = 0;
			for (const chunk of chunkIds(ids)) deleted += (await reviewsApi.bulkDeleteReviews({ ids: chunk })).data ?? 0;
			return deleted;
		},
		onSettled: invalidate
	});
	return { setStatus, remove };
}

/**
 * Customer reviews of every kind of item waiting for moderation (the sidebar badge). Polls every minute; only for Catalog users, who are the
 * ones the endpoint answers. Shares the `reviews` key root, so a moderation on the page refreshes it too.
 */
export function usePendingReviewsCount(enabled: boolean) {
	return useQuery<number>({
		queryKey: reviewKeys.pendingCount(),
		queryFn: async () => (await reviewsApi.getPendingReviewsCount()).data ?? 0,
		enabled,
		refetchInterval: 60_000,
		refetchOnWindowFocus: true
	});
}

/** The reports customers sent on a review, newest first (with who sent them). */
export function useReviewReports(reviewId: number | null) {
	return useQuery<ReviewReportToken[]>({
		queryKey: reviewKeys.reports(reviewId ?? 0),
		queryFn: async () => (await reviewsApi.getReviewReports(reviewId!)).data.items ?? [],
		enabled: reviewId != null && reviewId > 0
	});
}

/** Marks a review's open reports as handled; the grid's open-report counts refresh. */
export function useResolveReviewReports() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (reviewId: number) => (await reviewsApi.resolveReviewReports(reviewId)).data,
		onSettled: () => qc.invalidateQueries({ queryKey: reviewKeys.all })
	});
}

/** Removes a customer photo from its review (moderation). */
export function useDeleteReviewPhoto() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (photoId: number) => (await reviewsApi.deleteReviewPhoto(photoId)).data,
		onSettled: () => qc.invalidateQueries({ queryKey: reviewKeys.all })
	});
}
